import type { FC } from 'react'
import { useEffect, useState } from 'react'
import styles from './scroll-progress-bar.module.css'

export interface ScrollProgressBarProps {
  /** The scrollable content element to track. */
  scrollEl: HTMLElement | null
}

/**
 * Reading-position progress for the performance layout: a 2px accent line on the header's bottom edge.
 *
 * Owns the scroll subscription and progress state itself, so scroll events —
 * including the one emitted on every autoscroll animation frame — re-render
 * only this thin bar instead of the chord-heavy performance content that the
 * surrounding `Performance` component renders (GH #1549).
 */
export const ScrollProgressBar: FC<ScrollProgressBarProps> = props => {
  const { scrollEl } = props
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    if (!scrollEl) {
      return
    }
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl
      const progress = scrollHeight > clientHeight ? scrollTop / (scrollHeight - clientHeight) : 0
      setScrollProgress(Math.min(1, Math.max(0, progress)))
    }
    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [scrollEl])

  return (
    <div
      role="progressbar"
      aria-label="Position in song"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(scrollProgress * 100)}
      className={styles.progressBar}
    >
      <div className={styles.progressFill} style={{ width: `${scrollProgress * 100}%` }} />
    </div>
  )
}
